import { PlaceholderFillerModal } from "@/components/modals/PlaceholderFillerModal";
import { EditVariableModal } from "@/components/modals/EditVariableModal";
import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import { API_BASE_URL } from "../../config";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Checkbox } from "../../components/ui/checkbox";
import { toast } from "sonner";
import {
    FileText,
    Plus,
    Trash2,
    ChevronRight,
    ChevronLeft,
    Download,
    Shield,
    Sparkles,
    CheckCircle2,
    Loader2,
    ScrollText,
    Users,
    Settings2,
    ArrowRight,
    Copy,
    ExternalLink,
    Scale,
    Building2,
    Globe,
    Gauge,
    FileDown,
    RotateCcw,
    Eye,
    Maximize2,
    Minimize2,
    FolderOpen,
    GripVertical,
    ChevronDown,
    MoreHorizontal,
    FileEdit,
} from "lucide-react";
import {
    ResizableHandle,
    ResizablePanel,
    ResizablePanelGroup,
} from "../../components/ui/resizable";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useAuth } from "@/contexts/AuthContext";
import { useApiClient } from "@/hooks/use-api-client";
import { useAgentExecution } from "@/contexts/AgentExecutionContext";
import { supabase } from "@/lib/supabase";
import { createAuthHeaders } from "@/lib/auth-utils";
import { UsageTracker } from "@/lib/services/usage-tracker";
import { useDespachos } from "@/hooks/useDespachos";
import { getAgentBySlug } from "@/lib/agents";
import { useIsMobile } from "@/hooks/use-mobile";

// marked.setOptions({ breaks: true, gfm: true }); // Removed in favor of ReactMarkdown

// ─── Types ───────────────────────────────────────────────────────────────────

interface Party {
    name: string;
    identification: string;
    role: string;
    email?: string;
    address?: string;
    professional_card?: string;
}

const PARTY_ROLES = [
    { value: "Demandante", label: "Demandante" },
    { value: "Demandado", label: "Demandado" },
    { value: "Apoderado", label: "Apoderado" },
    { value: "Accionante", label: "Accionante" },
    { value: "Accionado", label: "Accionado" },
    { value: "Convocante", label: "Convocante" },
    { value: "Convocado", label: "Convocado" },
    { value: "Solicitante", label: "Solicitante" },
    { value: "Peticionario", label: "Peticionario" },
    { value: "Tercero Interesado", label: "Tercero Interesado" },
    { value: "Vinculado", label: "Vinculado" },
    { value: "Otorgante", label: "Otorgante" },
    { value: "Beneficiario", label: "Beneficiario" },
    { value: "Representante Legal", label: "Representante Legal" },
    { value: "Otro", label: "Otro" },
];

interface FormData {
    documentType: string;
    description: string;
    keyPoints: string;
    industry: string;
    jurisdiction: string;
    complexity: "simple" | "standard" | "complex" | "";
    parties: Party[];
    skill_id?: string;
    template_path?: string;
}

interface Skill {
    id: string;
    name: string;
    agent_type: string;
    description: string;
}

interface DocumentResult {
    status?: "processing" | "completed" | "failed" | "rejected";
    error?: string;
    message?: string;
    guard_rail_details?: {
        is_valid: boolean;
        confidence: number;
        reason: string;
    };
    document?: {
        content: string;
        sections?: string[];
        markdown?: boolean;
    };
    document_id?: string;
    document_type: string;
    jurisdiction?: string;
    document_url?: string;
    confidence_score?: number;
    colombian_compliance?: Record<string, unknown>;
    timestamp?: string;
}

// ─── Premium Styles ──────────────────────────────────────────────────────────

function PremiumStyles({ showForm }: { showForm: boolean }) {
    return (
        <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');

      .ddv2-root {
        font-family: 'Inter', system-ui, -apple-system, sans-serif;
        background: linear-gradient(135deg, #f8fafc 0%, #eef2ff 50%, #f1f5f9 100%);
        min-height: 100vh;
      }

      /* ─── Header ─── */
      .ddv2-header {
        background: linear-gradient(135deg, #1e1b4b 0%, #312e81 40%, #4338ca 100%);
        padding: 0.75rem 1.75rem;
        position: relative;
        overflow: hidden;
      }
      .ddv2-header::before {
        content: '';
        position: absolute;
        inset: 0;
        background: radial-gradient(ellipse at 80% 20%, rgba(129,140,248,0.2) 0%, transparent 60%),
                    radial-gradient(ellipse at 20% 80%, rgba(99,102,241,0.15) 0%, transparent 50%);
      }
      .ddv2-header-content {
        position: relative;
        z-index: 1;
      }

      /* ─── Wizard Steps ─── */
      .ddv2-step-indicator {
        display: flex;
        align-items: center;
        gap: 0;
      }
      .ddv2-step {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.5rem 1rem;
        border-radius: 999px;
        font-size: 0.8rem;
        font-weight: 600;
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        cursor: pointer;
        white-space: nowrap;
      }
      .ddv2-step.active {
        background: rgba(255,255,255,0.2);
        color: #fff;
        backdrop-filter: blur(8px);
        box-shadow: 0 0 20px rgba(129,140,248,0.3);
      }
      .ddv2-step.completed {
        background: rgba(34,197,94,0.2);
        color: #86efac;
      }
      .ddv2-step.upcoming {
        color: rgba(255,255,255,0.4);
      }
      .ddv2-step-connector {
        width: 2rem;
        height: 2px;
        background: rgba(255,255,255,0.15);
        flex-shrink: 0;
      }
      .ddv2-step-connector.done {
        background: rgba(34,197,94,0.5);
      }

      /* ─── Split Layout ─── */
      .ddv2-body {
        height: calc(100vh - 64px);
        overflow: hidden;
      }
      .ddv2-form-panel {
        background: #fff;
        height: 100%;
        overflow-y: auto;
        padding: 1.5rem 1.75rem;
        /* Hide scrollbar for Chrome, Safari, Opera */
        &::-webkit-scrollbar {
          display: none;
        }
        /* Hide scrollbar for IE, Edge, Firefox */
        -ms-overflow-style: none;
        scrollbar-width: none;
      }
      .ddv2-preview-panel {
        background: #f1f5f9;
        height: 100%;
        overflow-y: auto;
        padding: 2.5rem;
      }
      
      .ddv2-resize-handle {
        width: 6px;
        background: #e2e8f0;
        transition: background 0.2s;
        cursor: col-resize;
      }
      .ddv2-resize-handle:hover, .ddv2-resize-handle:active {
        background: #818cf8;
      }

      /* ─── Form Cards ─── */
      .ddv2-field-group {
        background: #fff;
        border: 1px solid #e2e8f0;
        border-radius: 1rem;
        padding: 1.5rem;
        margin-bottom: 1.25rem;
        transition: all 0.2s;
      }
      .ddv2-field-group:hover {
        border-color: #c7d2fe;
        box-shadow: 0 2px 12px rgba(99,102,241,0.06);
      }
      .ddv2-field-label {
        font-size: 0.8rem;
        font-weight: 700;
        color: #1e1b4b;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        margin-bottom: 0.5rem;
        display: flex;
        align-items: center;
        gap: 0.4rem;
      }

      /* ─── Party Cards ─── */
      .ddv2-party-card {
        background: linear-gradient(135deg, #fafafe 0%, #f5f3ff 100%);
        border: 1px solid #e9e5f5;
        border-radius: 0.75rem;
        padding: 1rem;
        margin-bottom: 0.75rem;
        position: relative;
        transition: all 0.2s;
      }
      .ddv2-party-card:hover {
        border-color: #c4b5fd;
      }

      /* ─── Preview ─── */
      .ddv2-preview-doc {
        background: #fff;
        border: 1px solid #cbd5e1;
        border-radius: 4px;
        padding: 3.5rem 4.5rem; /* Standardized screen margins */
        box-shadow: 0 20px 50px rgba(0,0,0,0.1);
        max-width: 1100px;
        width: 100%;
        margin: 0 auto;
        position: relative;
        overflow: hidden;
      }
      .ddv2-preview-doc::before {
        content: '';
        position: absolute;
        top: 0; left: 0; right: 0; height: 5px;
        background: #1e1b4b;
      }
      
      .legal-doc-content {
        font-family: 'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important;
        color: #1f2937 !important;
        line-height: 1.75 !important;
        text-align: left !important;
        width: 100% !important;
        font-size: 1rem !important;
        letter-spacing: -0.011em;
      }
      .legal-doc-content p { 
        margin-bottom: 1.25rem !important; 
        white-space: pre-wrap !important;
        text-indent: 0;
        color: #1f2937 !important;
      }
      .legal-doc-content h1, .legal-doc-content h2, .legal-doc-content h3 {
          font-family: 'Inter', system-ui, -apple-system, sans-serif !important;
          color: #111827 !important;
          text-align: left !important;
          text-transform: none !important;
          letter-spacing: -0.02em !important;
          font-weight: 600 !important;
          margin-bottom: 1rem !important;
          margin-top: 1.75rem !important;
      }
      .legal-doc-content h1 { font-size: 1.5rem !important; border-bottom: 1px solid #e5e7eb; padding-bottom: 0.5rem; }
      .legal-doc-content h2 { font-size: 1.25rem !important; border-left: none !important; padding-left: 0 !important; }
      .legal-doc-content h3 { font-size: 1.1rem !important; text-decoration: none !important; }
      
      .legal-doc-content ul, .legal-doc-content ol { 
        padding-left: 1.5rem !important; 
        margin-bottom: 1.25rem !important; 
      }
      .legal-doc-content li { 
        margin-bottom: 0.5rem !important; 
        line-height: 1.7 !important;
      }
      .legal-doc-content strong { 
        font-weight: 600 !important; 
        color: #111827 !important;
      }
      .legal-doc-content hr {
        border: 0;
        height: 1px;
        background: #cbd5e1;
        margin: 3rem 0;
      }
      /* Remove code block styling for better legal presentation */
      .legal-doc-content pre {
        background: transparent !important;
        padding: 0 !important;
        margin: 0 !important;
        font-family: inherit !important;
        white-space: pre-wrap !important;
        overflow: visible !important;
      }
      .legal-doc-content code {
        background: transparent !important;
        padding: 0 !important;
        font-family: inherit !important;
        font-size: inherit !important;
        color: inherit !important;
      }
      .legal-doc-content img, .legal-doc-content .signature-image {
        max-width: 100%;
        height: auto;
        border-bottom: 1px solid #ccc;
        margin: 10px 0;
        display: inline-block;
      }

      /* ─── Status Bar ─── */
      .ddv2-status-bar {
        background: linear-gradient(135deg, #1e1b4b, #312e81);
        border-radius: 1rem;
        padding: 1rem 1.5rem;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 1rem;
        margin-bottom: 1.5rem;
      }

      /* ─── Welcome State ─── */
      .ddv2-welcome {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        height: 100%;
        min-height: 400px;
        text-align: center;
        padding: 3rem;
      }
      .ddv2-welcome-icon {
        width: 80px;
        height: 80px;
        border-radius: 1.5rem;
        background: linear-gradient(135deg, #4338ca, #6366f1, #818cf8);
        display: flex;
        align-items: center;
        justify-content: center;
        margin-bottom: 1.5rem;
        box-shadow: 0 8px 32px rgba(99,102,241,0.25);
        animation: float 3s ease-in-out infinite;
      }
      @keyframes float {
        0%, 100% { transform: translateY(0); }
        50% { transform: translateY(-8px); }
      }

      /* ─── Compliance Badge ─── */
      .ddv2-compliance-badge {
        display: inline-flex;
        align-items: center;
        gap: 0.35rem;
        padding: 0.3rem 0.65rem;
        border-radius: 999px;
        font-size: 0.7rem;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.04em;
      }

      /* ─── Confidence Meter ─── */
      .ddv2-confidence-ring {
        width: 60px;
        height: 60px;
        border-radius: 50%;
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      .ddv2-confidence-ring svg {
        position: absolute;
        transform: rotate(-90deg);
      }

      /* ─── Animations ─── */
      @keyframes slideInLeft {
        from { opacity: 0; transform: translateX(-20px); }
        to { opacity: 1; transform: translateX(0); }
      }
      @keyframes slideInRight {
        from { opacity: 0; transform: translateX(20px); }
        to { opacity: 1; transform: translateX(0); }
      }
      @keyframes fadeInUp {
        from { opacity: 0; transform: translateY(16px); }
        to { opacity: 1; transform: translateY(0); }
      }
      .ddv2-animate-left { animation: slideInLeft 0.4s ease-out; }
      .ddv2-animate-right { animation: slideInRight 0.4s ease-out; }
      .ddv2-animate-up { animation: fadeInUp 0.5s ease-out; }

      /* ─── Loading Shimmer ─── */
      .ddv2-shimmer {
        background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
        background-size: 200% 100%;
        animation: shimmer 1.5s infinite;
        border-radius: 0.5rem;
      }
      @keyframes shimmer {
        0% { background-position: 200% 0; }
        100% { background-position: -200% 0; }
      }

      /* ─── Sticky Action Bar ─── */
      .ddv2-action-bar-sticky {
        position: sticky;
        bottom: -2.5rem; /* Account for parent padding */
        left: 0;
        right: 0;
        background: rgba(255, 255, 255, 0.8);
        backdrop-filter: blur(12px);
        border-top: 1px solid #e2e8f0;
        padding: 1rem 2rem;
        margin: 2rem -2.5rem -2.5rem -2.5rem;
        z-index: 40;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 1rem;
        box-shadow: 0 -4px 20px rgba(0,0,0,0.05);
      }

      /* ─── Responsive ─── */
      @media (max-width: 1024px) {
        .ddv2-body { flex-direction: column; }
        .ddv2-form-panel { width: 100%; min-width: 0; border-right: none; border-bottom: 1px solid #e2e8f0; }
        .ddv2-preview-panel { min-height: 50vh; }
      }
      @media (max-width: 768px) {
        .ddv2-action-bar-sticky {
          bottom: 0;
          margin: 1.5rem 0 0 0;
          padding: 0.75rem 1rem;
        }
      }

      /* ─── Dropdown ─── */
      .ddv2-dropdown {
        position: relative;
        display: inline-block;
      }
      .ddv2-dropdown-menu {
        display: none;
        position: absolute;
        bottom: 100%;
        right: 0;
        margin-bottom: 0.5rem;
        background: #fff;
        border: 1px solid #e2e8f0;
        border-radius: 1rem;
        box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1);
        z-index: 50;
        min-width: 200px;
        padding: 0.5rem;
        animation: slideInUp 0.2s ease-out;
      }
      @keyframes slideInUp {
        from { opacity: 0; transform: translateY(10px); }
        to { opacity: 1; transform: translateY(0); }
      }
      .ddv2-dropdown.open .ddv2-dropdown-menu {
        display: block;
      }
      .ddv2-dropdown-item {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        width: 100%;
        padding: 0.75rem 1rem;
        border-radius: 0.75rem;
        color: #1e293b;
        font-size: 0.75rem;
        font-weight: 600;
        transition: all 0.2s;
        text-align: left;
        background: none;
        border: none;
        cursor: pointer;
      }
      .ddv2-dropdown-item:hover {
        background: #f1f5f9;
        color: #4338ca;
      }
      .ddv2-rotate-180 {
        transform: rotate(180deg);
      }
    `}</style>
    );
}

// ─── ULDE Document Taxonomy ──────────────────────────────────────────────────

const ULDE_CATEGORIES: Record<string, { label: string; emoji: string; color: string }> = {
    judicial: { label: "Judicial", emoji: "⚖️", color: "#4338ca" },
    administrativo: { label: "Administrativo", emoji: "🏛️", color: "#0891b2" },
    comercial: { label: "Comercial / Privado", emoji: "🤝", color: "#059669" },
    "propiedad-intelectual": { label: "Propiedad Intelectual", emoji: "🧬", color: "#db2777" },
    laboral: { label: "Laboral", emoji: "👷", color: "#d97706" },
    notarial: { label: "Notarial", emoji: "📜", color: "#7c3aed" },
};

const ULDE_DOCUMENTS: Record<string, Record<string, string>> = {
    judicial: {
        accion_de_tutela: "Acción de Tutela",
        demanda_ordinaria_civil: "Demanda Ordinaria Civil",
        demanda_ejecutiva: "Demanda Ejecutiva",
        recurso_apelacion: "Recurso de Apelación",
        recurso_reposicion: "Recurso de Reposición",
        incidente_desacato: "Incidente de Desacato",
        medidas_cautelares: "Medidas Cautelares",
        accion_popular: "Acción Popular",
        demanda_laboral: "Demanda Laboral",
        contestacion_demanda: "Contestación de Demanda",
        alegatos_conclusion: "Alegatos de Conclusión",
    },
    administrativo: {
        derecho_de_peticion: "Derecho de Petición",
        recurso_reposicion_admin: "Recurso de Reposición Administrativo",
        recurso_apelacion_admin: "Recurso de Apelación Administrativo",
        queja_disciplinaria: "Queja Disciplinaria",
        solicitud_revocatoria: "Solicitud de Revocatoria Directa",
        solicitud_informacion: "Solicitud de Información Pública",
        reclamacion_administrativa: "Reclamación Administrativa",
    },
    comercial: {
        contrato_compraventa: "Contrato de Compraventa",
        contrato_arrendamiento: "Contrato de Arrendamiento",
        contrato_prestacion_servicios: "Contrato de Prestación de Servicios",
        acuerdo_confidencialidad: "Acuerdo de Confidencialidad (NDA)",
        otrosi_contractual: "Otrosí Contractual",
        terminacion_unilateral: "Terminación Unilateral",
        carta_cobro: "Carta de Cobro Prejurídico",
        acta_asamblea: "Acta de Asamblea",
        acta_junta_directiva: "Acta de Junta Directiva",
        reforma_estatutos: "Reforma de Estatutos",
        poder_general: "Poder General",
        poder_especial: "Poder Especial",
        certificacion_representacion: "Certificación de Representación Legal",
    },
    "propiedad-intelectual": {
        solicitud_patente: "Solicitud de Patente",
        oposicion_patente: "Oposición de Patente",
        registro_marca: "Registro de Marca",
        oposicion_marca: "Oposición de Marca",
    },
    laboral: {
        contrato_termino_indefinido: "Contrato a Término Indefinido",
        contrato_termino_fijo: "Contrato a Término Fijo",
        despido_justa_causa: "Despido con Justa Causa",
        liquidacion_laboral: "Liquidación Laboral",
        requerimiento_empleador: "Requerimiento al Empleador",
        queja_mintrabajo: "Queja ante MinTrabajo",
    },
    notarial: {
        poder_general_notarial: "Poder General Notarial",
        poder_especial_notarial: "Poder Especial Notarial",
        testamento_abierto: "Testamento Abierto",
        testamento_cerrado: "Testamento Cerrado",
        compraventa_inmueble: "Compraventa de Inmueble (Minuta)",
        hipoteca: "Constitución de Hipoteca",
        cancelacion_hipoteca: "Cancelación de Hipoteca",
    },
};

const JURISDICTIONS: Record<string, string> = {
    federal: "Nacional (Colombia)",
    bogota: "Bogotá D.C.",
    medellin: "Medellín",
    cali: "Cali",
    barranquilla: "Barranquilla",
    bucaramanga: "Bucaramanga",
    cartagena: "Cartagena",
    cucuta: "Cúcuta",
    pereira: "Pereira",
    manizales: "Manizales",
    other: "Otra Jurisdicción",
};

const COMPLEXITY_LEVELS: Record<string, { label: string; desc: string }> = {
    simple: { label: "Simple", desc: "Términos básicos, formato estándar" },
    standard: { label: "Estándar", desc: "Nivel de detalle normal, cláusulas completas" },
    complex: { label: "Complejo", desc: "Altamente detallado, múltiples condiciones" },
};

const CATEGORY_SCRIPTS: Record<string, string> = {
    judicial: "Necesito redactar un documento judicial de tipo [TIPO]. Los hechos principales son: [HECHOS]. Pretendo solicitar a la autoridad competente que [PRETENSIONES]. Es importante citar jurisprudencia relevante sobre [TEMA].",
    administrativo: "Solicito la elaboración de un [TIPO] dirigido a [ENTIDAD]. El motivo de mi solicitud es [MOTIVO]. Fundamento mi petición en el Artículo 23 de la Constitución Política y las leyes [LEYES].",
    comercial: "Requiero un [TIPO] entre [PARTE A] y [PARTE B]. El objeto del contrato es [OBJETO]. Las condiciones de pago acordadas son [CONDICIONES]. Incluir cláusulas de confidencialidad y terminación anticipada.",
    "propiedad-intelectual": "Deseo proteger [MARCA/PATENTE/OBRA] relacionada con [DESCRIPCIÓN]. El carácter innovador consiste en [DETALLES]. La jurisdicción de interés es [PAÍS/REGIÓN].",
    laboral: "Se requiere la creación de un [TIPO] para un empleado en el cargo de [CARGO]. El salario será de [VALOR] con una jornada de [HORARIO]. Incluir cláusulas de exclusividad y periodo de prueba.",
    notarial: "Solicito la minuta para un [TIPO]. Los comparecientes son [NOMBRES]. El acto consiste en [DETALLES DEL ACTO]. Por favor incluir las declaraciones necesarias ante notario público según la ley colombiana.",
};

// ─── Step definitions ────────────────────────────────────────────────────────

const STEPS = [
    { id: 1, label: "Tipo y descripción", icon: ScrollText },
    { id: 2, label: "Partes y puntos clave", icon: Users },
    { id: 3, label: "Jurisdicción y complejidad", icon: Settings2 },
];

const INITIAL_FORM: FormData = {
    documentType: "",
    description: "",
    keyPoints: "",
    industry: "",
    jurisdiction: "",
    complexity: "",
    parties: [
        { name: "", identification: "", role: "Demandante", email: "", address: "", professional_card: "" },
        { name: "", identification: "", role: "Demandado", email: "", address: "", professional_card: "" },
    ],
    template_path: "",
};

// ─── Main Component ──────────────────────────────────────────────────────────

export default function DocumentDraftingV2() {
    const navigate = useNavigate();
    const { user, profile } = useAuth();
    const isAdmin = profile?.role === "admin";
    const apiClient = useApiClient();
    const { startDraftingRun, getRun, resetRun } = useAgentExecution();
    const [docId, setDocId] = useState<string | null>(null);
    const [complementaryFiles, setComplementaryFiles] = useState<{ file_key: string, filename: string, chunks_count?: number }[]>([]);
    const [uploadingComplement, setUploadingComplement] = useState(false);
    const [existingFiles, setExistingFiles] = useState<{ file_key: string, filename: string }[]>([]);
    const [loadingExistingFiles, setLoadingExistingFiles] = useState(false);
    const [step, setStep] = useState(1);
    const [isCustomDocType, setIsCustomDocType] = useState(false);
    const [form, setForm] = useState<FormData>({
        ...INITIAL_FORM,
        skill_id: "none"
    });

    const [skills, setSkills] = useState<Skill[]>([]);
    const [loadingSkills, setLoadingSkills] = useState(false);
    const [activeSkillDetails, setActiveSkillDetails] = useState<any>(null);
    const [loadingSkillDetails, setLoadingSkillDetails] = useState(false);

    useEffect(() => {
        const fetchSkills = async () => {
            setLoadingSkills(true);
            try {
                const authHeaders = await createAuthHeaders();
                const res = await fetch(`${apiClient.baseUrl}/api/v1/contract-review/skills`, {
                    headers: authHeaders
                });
                if (res.ok) {
                    const data = await res.json();
                    // Map and detect agent_type from description prefix if missing
                    const mappedSkills = (data.skills || []).map((s: any) => {
                        let agentType = s.agent_type;
                        if (!agentType && s.description) {
                            if (s.description.includes("[LEGAL_RESEARCH]")) agentType = "legal_research";
                            else if (s.description.includes("[DOCUMENT_DRAFTING]")) agentType = "document_drafting";
                            else if (s.description.includes("[CONTRACT_REVIEW]")) agentType = "contract_review";
                        }
                        return { ...s, agent_type: agentType };
                    });

                    // Show all skills as requested by user for linking chatbot skills
                    const draftingSkills = mappedSkills;
                    setSkills(draftingSkills);
                }
            } catch (error) {
                console.error("Error fetching skills:", error);
            } finally {
                setLoadingSkills(false);
            }
        };
        fetchSkills();
    }, [apiClient.baseUrl]);

    // Fetch Skill details when selected
    useEffect(() => {
        const fetchSkillDetails = async () => {
            if (!form.skill_id || form.skill_id === "none") {
                setActiveSkillDetails(null);
                return;
            }

            setLoadingSkillDetails(true);
            try {
                const authHeaders = await createAuthHeaders();
                const res = await fetch(`${apiClient.baseUrl}/api/v1/contract-review/skills/${form.skill_id}`, {
                    headers: authHeaders
                });
                if (res.ok) {
                    const data = await res.json();
                    setActiveSkillDetails(data.skill);
                }
            } catch (error) {
                console.error("Error fetching skill details:", error);
            } finally {
                setLoadingSkillDetails(false);
            }
        };
        fetchSkillDetails();
    }, [form.skill_id, apiClient.baseUrl]);

    useEffect(() => {
        const fetchExistingFiles = async () => {
            if (!isAdmin) return;
            setLoadingExistingFiles(true);
            try {
                const authHeaders = await createAuthHeaders();
                const res = await fetch(`${apiClient.baseUrl}/api/v1/document-drafting/complementary-files`, {
                    headers: authHeaders
                });
                if (res.ok) {
                    const data = await res.json();
                    setExistingFiles(data);
                }
            } catch (error) {
                console.error("Error fetching existing complementary files:", error);
            } finally {
                setLoadingExistingFiles(false);
            }
        };
        fetchExistingFiles();
    }, [apiClient.baseUrl, isAdmin]);

    const [selectedCategory, setSelectedCategory] = useState("");
    const [showForm, setShowForm] = useState(true);
    const [showDownloadMenu, setShowDownloadMenu] = useState(false);
    const downloadRef = useRef<HTMLDivElement>(null);

    // ─── Click Outside Dropdown ──────────────────────────────────────────────
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (downloadRef.current && !downloadRef.current.contains(event.target as Node)) {
                setShowDownloadMenu(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Linking to Despachos Internal Processes
    const { processes: internalProcesses } = useDespachos();
    const [selectedCaseId, setSelectedCaseId] = useState<string>("none");

    // High-Fidelity V3 State
    const [hfTemplates, setHfTemplates] = useState<any[]>([]);
    const [selectedHfTemplate, setSelectedHfTemplate] = useState<string | null>(null);
    const [hfStructure, setHfStructure] = useState<{ sections: string[], variables: string[] }>({ sections: [], variables: [] });
    const [selectedSections, setSelectedSections] = useState<string[]>([]);
    const [hfLoading, setHfLoading] = useState(false);

    // Letterhead Templates
    const [letterheadTemplates, setLetterheadTemplates] = useState<any[]>([]);
    const [loadingLetterheads, setLoadingLetterheads] = useState(false);

    // Fetch Letterhead templates on mount
    useEffect(() => {
        const fetchLetterheads = async () => {
            setLoadingLetterheads(true);
            try {
                const authHeaders = await createAuthHeaders();
                const res = await fetch(`${apiClient.baseUrl}/api/v1/documents/templates`, {
                    headers: authHeaders
                });
                if (res.ok) {
                    const data = await res.json();
                    setLetterheadTemplates(data.templates || []);
                }
            } catch (err) {
                console.error("Error fetching letterhead templates:", err);
            } finally {
                setLoadingLetterheads(false);
            }
        };
        fetchLetterheads();
    }, [apiClient.baseUrl]);

    // Fetch High-Fidelity Templates on mount
    useEffect(() => {
        const fetchTemplates = async () => {
            try {
                const authHeaders = await createAuthHeaders();
                const response = await fetch(`${apiClient.baseUrl}/api/v1/document-drafting/high-fidelity-templates`, {
                    headers: authHeaders
                });
                if (response.ok) {
                    const data = await response.json();
                    if (Array.isArray(data)) {
                        setHfTemplates(data);
                    }
                }
            } catch (err) {
                console.error("Error fetching HF templates:", err);
            }
        };
        fetchTemplates();
    }, [apiClient.baseUrl]);

    // Handle HF Template selection
    const handleHfTemplateSelect = async (name: string) => {
        setSelectedHfTemplate(name);
        setHfLoading(true);
        try {
            const authHeaders = await createAuthHeaders();
            const response = await fetch(`${apiClient.baseUrl}/api/v1/document-drafting/template-structure/${encodeURIComponent(name)}`, {
                headers: authHeaders
            });
            if (response.ok) {
                const data = await response.json();
                setHfStructure(data);
                setSelectedSections(data.sections || []);
                // Update form with template info
                setIsCustomDocType(false);
                setForm(prev => ({
                    ...prev,
                    documentType: name,
                    description: `Minuta de Alta Fidelidad: ${name}. Verbatim assembly.`
                }));
            }
        } catch (err) {
            toast.error("Error al cargar la estructura de la plantilla");
        } finally {
            setHfLoading(false);
        }
    };

    const location = useLocation();
    const hasPrefilled = useRef(false);

    // Handle pre-fill from navigation state (e.g. from Contract Review)
    useEffect(() => {
        if (location.state && location.state.prefill && !hasPrefilled.current) {
            const { prefill } = location.state;
            setForm(prev => ({
                ...prev,
                documentType: prefill.documentType || prev.documentType,
                description: prefill.description || prev.description,
                keyPoints: prefill.keyPoints || prev.keyPoints,
                industry: prefill.category || prev.industry,
                jurisdiction: prefill.jurisdiction || 'federal', // Default to National
                complexity: prefill.complexity || 'standard', // Default to Standard
            }));

            if (prefill.category) {
                setSelectedCategory(prefill.category);
            }

            if (prefill.documentType) {
                const category = prefill.category || selectedCategory;
                const standardDocs = category ? Object.values(ULDE_DOCUMENTS[category] || {}) : [];
                if (prefill.documentType && !standardDocs.includes(prefill.documentType)) {
                    setIsCustomDocType(true);
                }
            }

            hasPrefilled.current = true;
            toast.success("Análisis vinculado: Campos pre-completados");
        }
    }, [location.state]);

    // Generate a unique run ID for this session to avoid non-UUID conflicts in DB
    const [draftingRunId] = useState(() => {
        try {
            return crypto.randomUUID();
        } catch (e) {
            return 'run-' + Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
        }
    });
    const currentRun = getRun(draftingRunId);

    const loading = currentRun?.status === "loading" || currentRun?.status === "streaming";
    const isMobile = useIsMobile();
    const [mobileTab, setMobileTab] = useState<"form" | "preview">("form");

    useEffect(() => {
        if ((loading || !!currentRun?.metadata) && isMobile) {
            setMobileTab("preview");
        }
    }, [loading, !!currentRun?.metadata, isMobile]);

    const [localResult, setLocalResult] = useState<DocumentResult | null>(null);
    const result = (currentRun?.metadata as DocumentResult) || localResult;
    const [customContent, setCustomContent] = useState<string | null>(null);
    const [isFillerModalOpen, setIsFillerModalOpen] = useState(false);
    const [editingVariable, setEditingVariable] = useState<string | null>(null);
    const hasAutoOpenedRef = useRef(false);

    const previewRef = useRef<HTMLDivElement>(null);

    // Sync docId when result changes
    useEffect(() => {
        if (result && !docId) {
            const res = result as any;
            const id =
                result.document_id ||
                res.id ||
                res.doc_id ||
                res.document?.id ||
                res.document?.document_id ||
                res.data?.id ||
                res.metadata?.id ||
                res.metadata?.document_id ||
                result.document_url?.match(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i)?.[0] ||
                (res.url || "").match(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i)?.[0] ||
                (res.document?.url || "").match(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i)?.[0];

            if (id) {
                setDocId(id);
            }
        }
    }, [result, docId]);

    const isCompleted = currentRun?.status === "completed" || !!result;
    const finalDocId = docId || (result as any)?.document_id || (result as any)?.id || draftingRunId;
    const downloadUrl = result?.document_url || (result as any)?.url || (result as any)?.document_path || (result as any)?.document?.url || (result as any)?.metadata?.url;

    const handleDownloadLocal = () => {
        if (!displayContent) return;
        const date = new Date().toISOString().split('T')[0];
        const intent = (form.documentType || 'documento').replace(/\s+/g, '_');
        const filename = `${intent}_${date}.md`;

        const blob = new Blob([displayContent], { type: 'text/markdown' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success("Documento descargado localmente (Markdown)");
    };

    const handleDownloadPDF = async () => {
        if (!displayContent) return;

        const loadingToast = toast.loading("Generando PDF de alta calidad...");

        try {
            const doc = new jsPDF();
            const margin = 20; // Professional margins (approx 2cm)
            const pageWidth = doc.internal.pageSize.getWidth();
            const textWidth = pageWidth - 2 * margin;
            let cursorY = 20;

            const title = form.documentType || "Documento Legal";

            // Professional Header
            doc.setFillColor(37, 99, 235); // Blue-600
            doc.rect(0, 0, pageWidth, 5, "F");

            doc.setFont("helvetica", "bold");
            doc.setFontSize(10);
            doc.setTextColor(100, 116, 139); // Slate-500
            doc.text("INTELIGENCIA JURÍDICA", margin, cursorY);

            doc.setFontSize(8);
            doc.text(`GENERADO EL ${new Date().toLocaleDateString('es-CO').toUpperCase()}`, pageWidth - margin - 40, cursorY);

            cursorY += 15;

            // Document Title
            doc.setFontSize(16);
            doc.setTextColor(30, 41, 59); // Slate-800
            const titleLines = doc.splitTextToSize(title.toUpperCase(), textWidth);
            doc.text(titleLines, margin, cursorY);
            cursorY += (titleLines.length * 7) + 5;

            // Divider
            doc.setDrawColor(226, 232, 240); // Slate-200
            doc.line(margin, cursorY, pageWidth - margin, cursorY);
            cursorY += 10;

            // Content Processing
            doc.setFont("times", "normal"); // Professional serif for legal
            doc.setFontSize(11);
            doc.setTextColor(51, 65, 85); // Slate-700

            // Basic Markdown -> PDF Text mapping
            const lines = displayContent.split('\n');
            for (const line of lines) {
                if (cursorY > 270) {
                    doc.addPage();
                    cursorY = 20;
                    doc.setFillColor(37, 99, 235);
                    doc.rect(0, 0, pageWidth, 5, "F");
                }

                let text = line.trim();
                if (!text) {
                    cursorY += 5;
                    continue;
                }

                // Headers
                if (text.startsWith('#')) {
                    doc.setFont("helvetica", "bold");
                    const level = (text.match(/^#+/) || [''])[0].length;
                    const fontSize = level === 1 ? 14 : level === 2 ? 12 : 11;
                    doc.setFontSize(fontSize);
                    text = text.replace(/^#+\s*/, '').toUpperCase();
                    const wrapped = doc.splitTextToSize(text, textWidth);
                    doc.text(wrapped, margin, cursorY);
                    cursorY += (wrapped.length * (fontSize * 0.5)) + 4;
                    doc.setFont("times", "normal");
                    doc.setFontSize(11);
                }
                // Bullet points
                else if (text.startsWith('-') || text.startsWith('*')) {
                    text = "• " + text.substring(1).trim();
                    const wrapped = doc.splitTextToSize(text, textWidth - 5);
                    doc.text(wrapped, margin + 5, cursorY);
                    cursorY += (wrapped.length * 6) + 2;
                }
                // Bold sections (simple detection)
                else if (text.includes('**')) {
                    doc.setFont("times", "bold");
                    const splitLine = text.split('**');
                    let currentX = margin;
                    splitLine.forEach((part, i) => {
                        doc.setFont("times", i % 2 === 1 ? "bold" : "normal");
                        const partWidth = doc.getTextWidth(part);
                        if (currentX + partWidth > pageWidth - margin) {
                            cursorY += 6;
                            currentX = margin;
                        }
                        doc.text(part, currentX, cursorY);
                        currentX += partWidth;
                    });
                    cursorY += 7;
                    doc.setFont("times", "normal");
                }
                // Normal text
                else {
                    const wrapped = doc.splitTextToSize(text, textWidth);
                    doc.text(wrapped, margin, cursorY);
                    cursorY += (wrapped.length * 6) + 2;
                }
            }

            const date = new Date().toISOString().split('T')[0];
            const intent = (title || 'documento').replace(/\s+/g, '_');
            const filename = `${intent}_${date}.pdf`;
            doc.save(filename);
            toast.dismiss(loadingToast);
            toast.success("PDF generado con éxito.");
        } catch (err) {
            console.error("PDF generation error:", err);
            toast.dismiss(loadingToast);
            toast.error("Error al generar PDF.");
        }
    };

    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files || e.target.files.length === 0) return;
        const file = e.target.files[0];

        setUploadingComplement(true);
        const toastId = toast.loading(`Cargando y vectorizando ${file.name}...`);

        try {
            const authHeaders = await createAuthHeaders();
            const formData = new FormData();
            formData.append("file", file);

            const res = await fetch(`${apiClient.baseUrl}/api/v1/document-drafting/upload-complement`, {
                method: "POST",
                headers: {
                    ...authHeaders
                },
                body: formData
            });

            if (res.ok) {
                const data = await res.json();
                setComplementaryFiles(prev => [...prev, {
                    file_key: data.file_key,
                    filename: data.filename,
                    chunks_count: data.chunks_count
                }]);
                setExistingFiles(prev => [...prev, {
                    file_key: data.file_key,
                    filename: data.filename
                }]);
                toast.success(`Archivo ${file.name} vectorizado correctamente (${data.chunks_count} fragmentos)`, { id: toastId });
            } else {
                const errData = await res.json().catch(() => ({ detail: "Error desconocido" }));
                toast.error(`Error al cargar archivo: ${errData.detail || "Verifique los permisos de administrador."}`, { id: toastId });
            }
        } catch (error) {
            console.error("Error uploading file:", error);
            toast.error("Error de red al cargar el archivo", { id: toastId });
        } finally {
            setUploadingComplement(false);
            e.target.value = "";
        }
    };

    const handleRemoveFile = (fileKey: string) => {
        setComplementaryFiles(prev => prev.filter(f => f.file_key !== fileKey));
        toast.info("Archivo complementario removido");
    };

    // ─── Form helpers ───────────────────────────────────────────────────────
    const updateField = <K extends keyof FormData>(key: K, value: FormData[K]) => {
        setForm((prev) => ({ ...prev, [key]: value }));
    };

    const addParty = (role: string = "Demandante") => {
        setForm((prev) => ({
            ...prev,
            parties: [
                ...prev.parties,
                { name: "", identification: "", role, email: "", address: "", professional_card: "" }
            ]
        }));
    };

    const removeParty = (index: number) => {
        setForm((prev) => {
            if (prev.parties.length <= 1) {
                return {
                    ...prev,
                    parties: [{ name: "", identification: "", role: "Demandante", email: "", address: "", professional_card: "" }]
                };
            }
            return {
                ...prev,
                parties: prev.parties.filter((_, i) => i !== index)
            };
        });
    };

    const updateParty = (index: number, field: keyof Party, value: string) => {
        setForm((prev) => {
            const parties = [...prev.parties];
            parties[index] = { ...parties[index], [field]: value };
            return { ...prev, parties };
        });
    };

    const handleAiAssist = () => {
        if (!form.documentType) {
            toast.error("Por favor seleccione un Tipo de Documento primero");
            return;
        }

        const type = form.documentType;
        const cat = selectedCategory || "general";

        let script = "";

        // Specific document scripts (short with placeholders)
        const DOCUMENT_SCRIPTS: Record<string, string> = {
            "Acción de Tutela": "Solicito Acción de Tutela contra [ENTIDAD/PERSONA] por vulneración de mis derechos fundamentales a [DERECHO 1] y [DERECHO 2]. Los hechos ocurrieron en [FECHA] cuando [DESCRIPCIÓN BREVE]. Pido que se ordene [PRETENSIÓN EXPRESA].",
            "Poder General": "Requiero un Poder General otorgado por [PODERDANTE] a favor de [APODERADO]. El objetivo es facultar para [ACTOS ADMINISTRATIVOS], [ACTOS JUDICIALES] y representación ante [ENTIDADES].",
            "Contrato de Arrendamiento": "Necesito un Contrato de Arrendamiento para un inmueble ubicado en [DIRECCIÓN]. El arrendatario es [NOMBRE] y el canon es de [VALOR]. El plazo será de [TIEMPO] y el uso será para [VIVIENDA/COMERCIAL].",
            "Cualquier otro": "Por favor genere un script para [TIPO] donde se detallen los siguientes puntos: [PUNTO 1], [PUNTO 2], involucrando a [PARTES] en el contexto de [CONTEXTO]."
        };

        const baseScript = DOCUMENT_SCRIPTS[type] || CATEGORY_SCRIPTS[cat] || "Describa aquí los hechos y antecedentes cronológicos de su documento...";

        updateField("description", baseScript);
        toast.success("Script generado con éxito");
    };

    // ─── Step validation ────────────────────────────────────────────────────
    const isStepValid = (s: number): boolean => {
        if (s === 1) return form.documentType.trim().length >= 3 && form.description.trim().length >= 10;
        if (s === 2) {
            const partiesValid = form.parties.length > 0 && form.parties.some((p) => p.name.trim().length > 0);
            // In HF mode, keyPoints is optional if variables are defined
            const keyPointsValid = selectedHfTemplate ? (form.keyPoints.trim().length >= 0) : (form.keyPoints.trim().length >= 5);
            return partiesValid && keyPointsValid;
        }
        if (s === 3) return !!form.jurisdiction && !!form.complexity;
        return false;
    };

    // ─── Submit ─────────────────────────────────────────────────────────────
    const handleSubmit = async () => {
        if (!isStepValid(1) || !isStepValid(2) || !isStepValid(3)) {
            toast.error("Por favor, complete todos los campos obligatorios.");
            return;
        }

        setLocalResult(null);
        setCustomContent(null);
        hasAutoOpenedRef.current = false;
        setDocId(null);

        const hf_metadata = selectedHfTemplate ? {
            template_name: selectedHfTemplate,
            selected_sections: selectedSections,
            variables: (form as any).hf_vars || {}
        } : null;

        // Track usage for dashboard
        if (user) {
            UsageTracker.trackDocumentGenerator(user.id, "Documento generado", form.documentType);
        }

        const agentData = getAgentBySlug("document-drafting");
        const payload = {
            ...form,
            hf_metadata,
            case_id: selectedCaseId !== "none" ? selectedCaseId : undefined,
            skill_id: form.skill_id === "none" ? undefined : form.skill_id,
            template_path: form.template_path === "none" || !form.template_path ? undefined : form.template_path,
            complementary_file_keys: complementaryFiles.length > 0 ? complementaryFiles.map(f => f.file_key) : undefined
        };
        await startDraftingRun(draftingRunId, payload, apiClient, user?.id || null, agentData?.system_prompt);

        // Reset the wizard to the first step and CLEAR the fields for a new draft
        // But keep the 'result' in the run state so the document remains visible on the right
        setStep(1);
        setIsCustomDocType(false);
        setForm({ ...INITIAL_FORM, skill_id: "none" });
        setSelectedCategory("");
        setSelectedHfTemplate(null);
        setHfStructure({ sections: [], variables: [] });
        setSelectedSections([]);
        setComplementaryFiles([]);
    };

    const handleReset = () => {
        resetRun(draftingRunId);
        setIsCustomDocType(false);
        setForm({ ...INITIAL_FORM, skill_id: "none" });
        setLocalResult(null);
        setSelectedCategory("");
        setSelectedHfTemplate(null);
        setHfStructure({ sections: [], variables: [] });
        setSelectedSections([]);
        setComplementaryFiles([]);
        setStep(1);
        toast.info("Formulario reiniciado");
    };

    const copyToClipboard = async (text: string) => {
        try {
            await navigator.clipboard.writeText(text);
            toast.success("Contenido copiado al portapapeles");
        } catch { toast.error("Error al copiar"); }
    };

    // ─── Rendered content ──────────────────────────────────────────────────
    const displayContent = useMemo(() => {
        if (customContent !== null) return customContent;
        let content = result?.document?.content || (result as any)?.content || (result as any)?.answer || "";

        if (!content && typeof result === 'string') {
            content = result;
        }

        if (typeof content !== 'string') return "";

        // Strip leading/trailing code block markers that the LLM often adds despite instructions
        return content.replace(/```(?:markdown|txt|text|html|json)?\s*/gi, "").replace(/```\s*$/g, "").trim();
    }, [result, customContent]);

        const extractPlaceholders = (text: string): string[] => {
        const matches = text.match(/\[(?![^\]]*\]\()([^\[\]\r\n]{2,120})\]/g);
        if (!matches) return [];
        const cleaned = matches
            .map((m) => m.slice(1, -1).trim())
            .filter(
                (m) =>
                    !m.startsWith('DOCUMENTO') &&
                    !m.startsWith('CONTEXTO') &&
                    !m.startsWith('FIN') &&
                    !m.startsWith('ADVERTENCIA') &&
                    !m.startsWith('FUENTE') &&
                    !m.startsWith('http://') &&
                    !m.startsWith('https://')
            );
        return Array.from(new Set(cleaned));
    };

    const handleReplaceSinglePlaceholder = (targetPlaceholder: string) => {
        setEditingVariable(targetPlaceholder);
    };

    const handleSaveSingleVariable = (newVal: string) => {
        if (!editingVariable) return;
        const targetPlaceholder = editingVariable;
        const escaped = targetPlaceholder.replace(/[-/\^$*+?.()|[\]{}]/g, '\$&');
        const pattern = new RegExp(`\\[${escaped}\\]`, 'g');
        setCustomContent(prev => {
            const base = prev !== null ? prev : displayContent;
            return base.replace(pattern, newVal.trim());
        });
        toast.success(`Variable [${targetPlaceholder}] actualizada`);
        setEditingVariable(null);
    };

    const renderNodeWithChips = (children: React.ReactNode): React.ReactNode => {
        return React.Children.map(children, (child) => {
            if (typeof child === 'string') {
                const parts = child.split(/(\[(?![^\]]*\]\()[^\[\]\r\n]{2,120}\])/g);
                if (parts.length === 1) return child;
                return parts.map((part, index) => {
                    const match = part.match(/^\[((?![^\]]*\]\()[^\[\]\r\n]{2,120})\]$/);
                    if (match) {
                        const rawKey = match[1].trim();
                        if (
                            rawKey.startsWith('DOCUMENTO') ||
                            rawKey.startsWith('CONTEXTO') ||
                            rawKey.startsWith('FIN') ||
                            rawKey.startsWith('ADVERTENCIA') ||
                            rawKey.startsWith('FUENTE') ||
                            rawKey.startsWith('http')
                        ) {
                            return part;
                        }
                        return (
                            <button
                                key={index}
                                type="button"
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleReplaceSinglePlaceholder(rawKey);
                                }}
                                title={`Click para editar variable: [${rawKey}]`}
                                className="inline-flex items-center gap-1 mx-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-amber-100 hover:bg-amber-200 text-amber-900 border border-amber-300 shadow-sm transition-all hover:scale-105 cursor-pointer align-baseline select-none"
                            >
                                <span className="opacity-70 text-[10px]">✏️</span>
                                <span>[{rawKey}]</span>
                            </button>
                        );
                    }
                    return part;
                });
            }
            if (React.isValidElement(child) && (child as any).props && (child as any).props.children) {
                return React.cloneElement(child, {
                    ...(child as any).props,
                    children: renderNodeWithChips((child as any).props.children)
                });
            }
            return child;
        });
    };

    const placeholders = useMemo(() => {
        return extractPlaceholders(displayContent);
    }, [displayContent]);

    const handleReplaceMultiplePlaceholders = (replacements: Record<string, string>) => {
        let updated = displayContent;
        let count = 0;
        for (const [key, val] of Object.entries(replacements)) {
            if (val && val.trim()) {
                const escapedKey = key.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');
                const targetPattern = new RegExp(`\\\[${escapedKey}\\\]`, 'g');
                updated = updated.replace(targetPattern, val.trim());
                count++;
            }
        }
        setCustomContent(updated);
        toast.success(`${count} campos completados exitosamente en el documento`);
    };

    // Auto-opening disabled: bulk modal only in LegalEditor; click chips in document drafting v2
    /*
    useEffect(() => {
        if (isCompleted && placeholders.length > 0 && !hasAutoOpenedRef.current) {
            hasAutoOpenedRef.current = true;
            setIsFillerModalOpen(true);
        }
    }, [isCompleted, placeholders.length]);
    */

    // ─── Confidence color ──────────────────────────────────────────────────
    const rawConfidence = result?.confidence_score ?? result?.guard_rail_details?.confidence ?? (result as any)?.confidence ?? 0.85; // Default to 85 if missing but result exists
    const confidencePercent = Math.round(rawConfidence * 100);
    const confidenceColor = confidencePercent >= 80 ? "#22c55e" : confidencePercent >= 50 ? "#eab308" : "#ef4444";
    const circumference = 2 * Math.PI * 24;
    const strokeDash = (confidencePercent / 100) * circumference;

    // ─── Render ─────────────────────────────────────────────────────────────
    return (
        <div className="ddv2-root">
            <PremiumStyles showForm={showForm} />

            {/* ── Header ── */}
            <div className="ddv2-header">
                <div className="ddv2-header-content">
                    <div className="flex items-center justify-between flex-wrap gap-4">
                        <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-lg bg-white/10 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
                                <FileText className="h-4 w-4 text-white" />
                            </div>
                            <div>
                                <h1 className="text-base font-bold text-white tracking-tight">Redactor de Documentos V2</h1>
                                <p className="text-indigo-200 text-[11px] leading-tight">Generación inteligente de documentos jurídicos colombianos</p>
                            </div>
                        </div>

                        {/* Header Actions */}
                        <div className="flex items-center gap-3">
                            <Button
                                variant="ghost"
                                onClick={() => navigate("/dashboard/documents")}
                                className="text-white hover:bg-white/10 gap-2 border border-white/20 h-8 text-xs px-3"
                            >
                                <ScrollText className="h-3.5 w-3.5" /> Mis Documentos
                            </Button>

                            {/* Step indicator */}
                            <div className="ddv2-step-indicator">
                                {STEPS.map((s, i) => (
                                    <div key={s.id} className="flex items-center">
                                        <button
                                            onClick={() => { if (s.id < step || (s.id === step + 1 && isStepValid(step))) setStep(s.id); }}
                                            className={`ddv2-step ${step === s.id ? "active" : step > s.id ? "completed" : "upcoming"}`}
                                        >
                                            {step > s.id ? <CheckCircle2 className="h-4 w-4" /> : <s.icon className="h-4 w-4" />}
                                            <span className="hidden sm:inline">{s.label}</span>
                                        </button>
                                        {i < STEPS.length - 1 && (
                                            <div className={`ddv2-step-connector ${step > s.id ? "done" : ""}`} />
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Mobile Tab Switcher */}
            {isMobile && (
                <div className="flex items-center justify-center p-2.5 bg-slate-900 border-b border-indigo-950/60 sticky top-0 z-30">
                    <div className="flex bg-white/10 p-1 rounded-xl w-full max-w-sm">
                        <button
                            type="button"
                            onClick={() => setMobileTab("form")}
                            className={`flex-1 py-2 text-xs font-bold rounded-lg transition-all flex items-center justify-center gap-1.5 ${mobileTab === "form" ? "bg-white text-indigo-950 shadow-sm" : "text-white/70 hover:text-white"}`}
                        >
                            <FileEdit className="w-3.5 h-3.5" />
                            <span>Formulario (Paso {step}/3)</span>
                        </button>
                        <button
                            type="button"
                            onClick={() => setMobileTab("preview")}
                            className={`flex-1 py-2 text-xs font-bold rounded-lg transition-all flex items-center justify-center gap-1.5 ${mobileTab === "preview" ? "bg-white text-indigo-950 shadow-sm" : "text-white/70 hover:text-white"}`}
                        >
                            <Eye className="w-3.5 h-3.5" />
                            <span>Vista Previa</span>
                            {result && <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />}
                        </button>
                    </div>
                </div>
            )}

            {/* ── Body ── */}
            <div className="ddv2-body">
                <ResizablePanelGroup direction={isMobile ? "vertical" : "horizontal"}>
                    {(!isMobile || mobileTab === "form") && showForm && (
                        <>
                            <ResizablePanel defaultSize={isMobile ? 100 : 33} minSize={isMobile ? 100 : 25} maxSize={isMobile ? 100 : 50}>
                                <div className={`ddv2-form-panel ${isMobile ? 'w-full pb-36 border-none' : 'h-full'} overflow-y-auto`}>
                                    {/* Step 1 Content */}
                                    {step === 1 && (
                                        <div className="ddv2-animate-left space-y-6">
                                            {/* 1. Category Selection */}
                                            <div className="ddv2-field-group p-4 bg-white border border-slate-100 rounded-2xl shadow-sm">
                                                <div className="ddv2-field-label text-indigo-900 font-black uppercase tracking-wider mb-4 flex items-center gap-2">
                                                    <Scale className="h-4 w-4 text-indigo-500" /> Categoría del Documento
                                                </div>
                                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                                    {Object.entries(ULDE_CATEGORIES).map(([id, cat]) => (
                                                        <button
                                                            key={id}
                                                            onClick={() => {
                                                                setSelectedCategory(id);
                                                                updateField("documentType", "");
                                                                setIsCustomDocType(false);
                                                            }}
                                                            className={`flex flex-col items-center justify-center p-2 sm:p-2.5 rounded-xl text-center border transition-all duration-300 group ${selectedCategory === id ? 'bg-indigo-50/50 border-indigo-500 ring-2 ring-indigo-500/5' : 'bg-white border-slate-100 hover:border-slate-200 hover:bg-slate-50/50'}`}
                                                        >
                                                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-lg shadow-sm transition-transform group-hover:scale-110 ${selectedCategory === id ? 'bg-indigo-500 text-white' : 'bg-slate-50 text-slate-400 group-hover:text-indigo-500'}`}>
                                                                {cat.emoji}
                                                            </div>
                                                            <span className={`text-[10px] font-bold leading-tight mt-1.5 ${selectedCategory === id ? 'text-indigo-600' : 'text-slate-500'}`}>
                                                                {cat.label}
                                                            </span>
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>

                                            {/* 2. Document Type Selection (Dropdown as requested) */}
                                            <div className="ddv2-field-group p-4 bg-white border border-slate-100 rounded-2xl shadow-sm">
                                                <div className="ddv2-field-label text-indigo-900 font-black uppercase tracking-wider mb-3 flex items-center gap-2">
                                                    <FileText className="h-4 w-4 text-indigo-500" /> Tipo de Documento
                                                </div>
                                                <Select
                                                    value={isCustomDocType ? "custom" : form.documentType}
                                                    onValueChange={(v) => {
                                                        if (v === "custom") {
                                                            setIsCustomDocType(true);
                                                            updateField("documentType", "");
                                                        } else {
                                                            setIsCustomDocType(false);
                                                            updateField("documentType", v);
                                                        }
                                                    }}
                                                    disabled={!selectedCategory}
                                                >
                                                    <SelectTrigger className="h-12 border-2 border-slate-100 hover:border-indigo-200 rounded-xl bg-white transition-all">
                                                        <SelectValue placeholder={selectedCategory ? "Seleccione el tipo de documento..." : "Primero seleccione una categoría arriba"} />
                                                    </SelectTrigger>
                                                    <SelectContent className="rounded-xl border-slate-200 shadow-2xl bg-white z-[100]">
                                                        {selectedCategory && Object.entries(ULDE_DOCUMENTS[selectedCategory] || {}).map(([key, label]) => (
                                                            <SelectItem key={key} value={label} className="py-2.5 font-medium text-slate-700">
                                                                {label}
                                                            </SelectItem>
                                                        ))}
                                                        <SelectItem value="custom" className="py-2.5 font-bold text-indigo-600 italic">
                                                            + Otro (Especificar manualmente)
                                                        </SelectItem>
                                                    </SelectContent>
                                                </Select>

                                                {/* Manual input if they chose "Otro" or want to type */}
                                                {isCustomDocType && (
                                                    <div className="mt-3 ddv2-animate-up">
                                                        <Input
                                                            placeholder="Escriba el nombre del documento aquí..."
                                                            className="h-11 border-indigo-200 focus:ring-indigo-500 rounded-xl"
                                                            value={form.documentType}
                                                            onChange={(e) => updateField("documentType", e.target.value)}
                                                            autoFocus
                                                        />
                                                    </div>
                                                )}
                                            </div>

                                            {/* 3. Skill & Playbook (Always accessible in Step 1) */}
                                            {/* <div className="ddv2-field-group p-4 bg-white border border-slate-100 rounded-2xl shadow-sm">
                                                <div className="ddv2-field-label text-indigo-900 font-black uppercase tracking-wider mb-3 flex items-center gap-2">
                                                    <Shield className="h-4 w-4 text-indigo-500" /> Skill / Playbook
                                                </div>
                                                <Select value={form.skill_id} onValueChange={(v) => updateField("skill_id", v)}>
                                                    <SelectTrigger className="h-12 border-2 border-slate-100 rounded-xl bg-white transition-all">
                                                        <SelectValue placeholder="Seleccione un Skill (Opcional)..." />
                                                    </SelectTrigger>
                                                    <SelectContent className="rounded-xl border-slate-200 shadow-2xl bg-white z-[100]">
                                                        <SelectItem value="none" className="font-medium">Sin Skill (Redacción Estándar)</SelectItem>
                                                        {skills.map(s => (
                                                            <SelectItem key={s.id} value={s.id} className="py-3">
                                                                <div className="flex flex-col">
                                                                    <span className="font-bold">{s.name}</span>
                                                                    <span className="text-[10px] text-slate-400 line-clamp-1">{s.description}</span>
                                                                </div>
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                                {activeSkillDetails && (
                                                    <div className="mt-3 p-3 bg-indigo-50/50 border border-indigo-100 rounded-xl ddv2-animate-up">
                                                        <div className="text-[10px] font-black text-indigo-600 uppercase tracking-wider mb-1 flex items-center gap-1">
                                                            <Sparkles className="h-3 w-3" /> Playbook Activo
                                                        </div>
                                                        <div className="text-xs text-slate-600 leading-relaxed italic line-clamp-2">
                                                            "{activeSkillDetails.description}"
                                                        </div>
                                                    </div>
                                                )}
                                            </div> */}

                                            <div className="ddv2-field-group">
                                                <div className="flex items-center justify-between mb-1">
                                                    <div className="ddv2-field-label mb-0 text-indigo-950 font-bold flex items-center gap-2">
                                                        <Users className="h-4 w-4 text-indigo-500" /> Hechos y Antecedentes Cronológicos
                                                    </div>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        className="h-7 text-[10px] font-black text-indigo-600 hover:bg-indigo-50 px-2"
                                                        onClick={handleAiAssist}
                                                    >
                                                        <Sparkles className="h-3 w-3 mr-1" /> AUTO-COMPLETAR
                                                    </Button>
                                                </div>
                                                <p className="text-xs text-slate-500 mb-3 font-normal leading-relaxed">
                                                    Indique de manera secuencial los antecedentes fácticos, fechas y circunstancias que fundamentan la solicitud.
                                                </p>
                                                <Textarea
                                                    placeholder="Indique de manera secuencial los antecedentes fácticos, fechas y circunstancias que fundamentan la solicitud..."
                                                    value={form.description}
                                                    onChange={(e) => updateField("description", e.target.value)}
                                                    className="min-h-[160px] border-slate-200 focus:border-indigo-500 transition-all resize-none text-sm leading-relaxed"
                                                />
                                            </div>

                                            {/* Archivos Complementarios (Solo Administradores) */}
                                            {isAdmin && (
                                                <div className="ddv2-field-group p-4 bg-indigo-50/20 border border-indigo-100 rounded-2xl">
                                                    <div className="ddv2-field-label text-indigo-950 font-bold mb-2 flex items-center gap-2">
                                                        <Plus className="h-4 w-4 text-indigo-500" /> Archivos Complementarios (Admin)
                                                    </div>
                                                    <p className="text-[10px] text-indigo-500 leading-normal mb-3">
                                                        Carga archivos (PDF, DOCX o TXT) que complementen o sirvan de referencia para la redacción de este documento. La IA los analizará y vectorizará automáticamente.
                                                    </p>

                                                    <div className="relative border-2 border-dashed border-slate-200 hover:border-indigo-400 bg-white rounded-xl p-4 transition-all text-center flex flex-col items-center justify-center cursor-pointer">
                                                        <input
                                                            type="file"
                                                            accept=".pdf,.docx,.txt"
                                                            onChange={handleFileUpload}
                                                            disabled={uploadingComplement}
                                                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
                                                        />
                                                        {uploadingComplement ? (
                                                            <div className="flex flex-col items-center gap-2">
                                                                <Loader2 className="h-6 w-6 text-indigo-500 animate-spin" />
                                                                <span className="text-xs text-indigo-600 font-medium">Vectorizando y procesando contenido...</span>
                                                            </div>
                                                        ) : (
                                                            <div className="flex flex-col items-center gap-1.5">
                                                                <Plus className="h-5 w-5 text-indigo-500" />
                                                                <span className="text-xs font-semibold text-slate-600">Selecciona o arrastra un archivo</span>
                                                                <span className="text-[9px] text-slate-400">PDF, DOCX, TXT hasta 15MB</span>
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Lista de archivos cargados */}
                                                    {complementaryFiles.length > 0 && (
                                                        <div className="mt-4 space-y-2">
                                                            <div className="text-[9px] font-bold text-slate-400 uppercase tracking-wide">Archivos Cargados ({complementaryFiles.length})</div>
                                                            {complementaryFiles.map((file) => (
                                                                <div key={file.file_key} className="flex items-center justify-between p-2 bg-white border border-slate-100 rounded-lg text-xs animate-fadeIn">
                                                                    <div className="flex items-center gap-2 truncate">
                                                                        <FileText className="h-3.5 w-3.5 text-indigo-500 flex-shrink-0" />
                                                                        <span className="font-semibold text-slate-700 truncate">{file.filename}</span>
                                                                        {file.chunks_count && (
                                                                            <span className="text-[9px] text-indigo-500 font-medium">({file.chunks_count} fgs)</span>
                                                                        )}
                                                                    </div>
                                                                    <button
                                                                        onClick={() => handleRemoveFile(file.file_key)}
                                                                        className="text-slate-400 hover:text-red-500 p-1"
                                                                    >
                                                                        <Trash2 className="h-3.5 w-3.5" />
                                                                    </button>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}

                                                    {/* Archivos vectorizados previamente */}
                                                    {existingFiles.length > 0 && (
                                                        <div className="mt-4 pt-3 border-t border-slate-100">
                                                            <div className="text-[10px] font-black text-indigo-950 uppercase tracking-wider mb-2 flex items-center gap-1">
                                                                <FolderOpen className="h-3 w-3 text-indigo-500" /> Usar Archivo de Referencia Existente
                                                            </div>
                                                            <div className="max-h-[140px] overflow-y-auto space-y-1.5 pr-1">
                                                                {existingFiles.map((file) => {
                                                                    const isSelected = complementaryFiles.some(f => f.file_key === file.file_key);
                                                                    return (
                                                                        <label
                                                                            key={file.file_key}
                                                                            className={`flex items-center gap-2 p-2 rounded-lg border text-[11px] cursor-pointer transition-all ${isSelected ? 'bg-indigo-50/50 border-indigo-200' : 'bg-slate-50/50 border-slate-100 hover:bg-slate-50'}`}
                                                                        >
                                                                            <Checkbox
                                                                                checked={isSelected}
                                                                                onCheckedChange={(checked) => {
                                                                                    if (checked) {
                                                                                        setComplementaryFiles(prev => [...prev, file]);
                                                                                    } else {
                                                                                        setComplementaryFiles(prev => prev.filter(f => f.file_key !== file.file_key));
                                                                                    }
                                                                                }}
                                                                            />
                                                                            <span className="truncate font-medium text-slate-700">{file.filename}</span>
                                                                        </label>
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            <div className="flex justify-end mt-4">
                                                <Button
                                                    onClick={() => setStep(2)}
                                                    disabled={!isStepValid(1)}
                                                    className="bg-slate-900 hover:bg-slate-800 text-white gap-2 px-6 rounded-xl"
                                                >
                                                    Siguiente <ChevronRight className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        </div>
                                    )}

                                    {/* Step 2 Content */}
                                    {step === 2 && (
                                        <div className="ddv2-animate-left">
                                            {/* High Fidelity Section */}
                                            {selectedHfTemplate && (
                                                <div className="mb-4 p-4 bg-orange-50 border border-orange-100 rounded-2xl">
                                                    <div className="ddv2-field-label text-orange-700">
                                                        <Sparkles className="h-4 w-4 text-orange-600" /> Parámetros de Alta Fidelidad
                                                    </div>
                                                    <div className="grid grid-cols-1 gap-3">
                                                        {hfStructure.variables.map((variable) => (
                                                            <div key={variable}>
                                                                <label className="text-[10px] font-bold text-slate-500 uppercase mb-1 block">{variable}</label>
                                                                <Input
                                                                    placeholder={`Valor para ${variable}...`}
                                                                    className="text-sm border-orange-100"
                                                                    value={(form as any).hf_vars?.[variable] || ""}
                                                                    onChange={(e) => {
                                                                        setForm(prev => ({
                                                                            ...prev,
                                                                            hf_vars: { ...((prev as any).hf_vars || {}), [variable]: e.target.value }
                                                                        }));
                                                                    }}
                                                                />
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            <div className="ddv2-field-group p-4 bg-white border border-slate-100 rounded-2xl shadow-sm">
                                                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                                                    <div className="text-sm font-black text-slate-900 flex items-center gap-2">
                                                        <Users className="h-4 w-4 text-amber-700" /> 2. Partes Procesales ({form.parties.length})
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <button
                                                            type="button"
                                                            onClick={() => addParty(form.parties.some(p => p.role === "Demandante") && !form.parties.some(p => p.role === "Demandado") ? "Demandado" : "Demandante")}
                                                            className="flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-md border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 shadow-sm transition-all"
                                                        >
                                                            <Plus className="h-3 w-3 text-slate-500" /> Parte
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => addParty("Apoderado")}
                                                            className="flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-md border border-amber-300 bg-amber-50/70 hover:bg-amber-100 text-amber-800 shadow-sm transition-all"
                                                        >
                                                            <Plus className="h-3 w-3 text-amber-600" /> Apoderado T.P.
                                                        </button>
                                                    </div>
                                                </div>

                                                <div className="space-y-3">
                                                    {form.parties.map((party, i) => {
                                                        const isApoderado = party.role === "Apoderado";
                                                        return (
                                                            <div
                                                                key={i}
                                                                className={`rounded-xl p-3 space-y-2.5 transition-all ${
                                                                    isApoderado
                                                                        ? "border border-amber-300/80 bg-amber-50/20 shadow-sm"
                                                                        : "border border-slate-200 bg-white shadow-sm"
                                                                }`}
                                                            >
                                                                {/* Fila 1: Selector de Rol, Nombre, Acciones */}
                                                                <div className="flex items-center gap-2">
                                                                    <Select
                                                                        value={party.role || "Demandante"}
                                                                        onValueChange={(v) => updateParty(i, "role", v)}
                                                                    >
                                                                        <SelectTrigger className={`h-9 w-[140px] shrink-0 font-bold rounded-lg border text-xs sm:text-sm bg-white transition-all ${
                                                                            isApoderado ? "text-amber-800 border-amber-300" : "text-amber-800 border-slate-200"
                                                                        }`}>
                                                                            <SelectValue placeholder="Rol" />
                                                                        </SelectTrigger>
                                                                        <SelectContent className="rounded-xl border-slate-200 shadow-xl bg-white z-[100]">
                                                                            {PARTY_ROLES.map((r) => (
                                                                                <SelectItem key={r.value} value={r.value} className="font-semibold text-slate-700 py-2">
                                                                                    {r.label}
                                                                                </SelectItem>
                                                                            ))}
                                                                        </SelectContent>
                                                                    </Select>

                                                                    <Input
                                                                        value={party.name}
                                                                        onChange={(e) => updateParty(i, "name", e.target.value)}
                                                                        placeholder={isApoderado ? "Nombre del apoderado (ej. Dra. Valentina Gómez Restrepo)" : "Nombre completo / Razón social"}
                                                                        className="h-9 flex-1 text-sm font-medium border-slate-200 rounded-lg bg-white placeholder:text-slate-400"
                                                                    />

                                                                    {/* Botón opciones rápidas */}
                                                                    <button
                                                                        type="button"
                                                                        title="Copiar información de la parte"
                                                                        onClick={() => {
                                                                            const details = [
                                                                                party.role && `Rol: ${party.role}`,
                                                                                party.name && `Nombre: ${party.name}`,
                                                                                party.identification && `Doc: ${party.identification}`,
                                                                                party.professional_card && `T.P: ${party.professional_card}`,
                                                                                party.email && `Email: ${party.email}`,
                                                                                party.address && `Dir: ${party.address}`
                                                                            ].filter(Boolean).join(" | ");
                                                                            if (details) {
                                                                                copyToClipboard(details);
                                                                            } else {
                                                                                toast.info("Complete los datos de la parte para copiar.");
                                                                            }
                                                                        }}
                                                                        className="h-8 px-2 flex items-center justify-center text-slate-400 hover:text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-md transition-colors"
                                                                    >
                                                                        <MoreHorizontal className="h-4 w-4" />
                                                                    </button>

                                                                    {/* Botón eliminar */}
                                                                    <button
                                                                        type="button"
                                                                        title="Eliminar parte"
                                                                        onClick={() => removeParty(i)}
                                                                        className="h-8 w-8 flex items-center justify-center text-red-500 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors"
                                                                    >
                                                                        <Trash2 className="h-4 w-4" />
                                                                    </button>
                                                                </div>

                                                                {/* Fila 2: Inputs secundarios */}
                                                                {isApoderado ? (
                                                                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
                                                                        <Input
                                                                            value={party.identification}
                                                                            onChange={(e) => updateParty(i, "identification", e.target.value)}
                                                                            placeholder="Doc. (ej. CC 52.412.980 de Bogotá)"
                                                                            className="h-8 text-xs rounded-md border-slate-200 bg-white"
                                                                        />
                                                                        <Input
                                                                            value={party.professional_card || ""}
                                                                            onChange={(e) => updateParty(i, "professional_card", e.target.value)}
                                                                            placeholder="T.P. (ej. 312.450 del C.S. de la J.)"
                                                                            className="h-8 text-xs rounded-md border-amber-300 bg-amber-50/70 text-amber-950 font-medium placeholder:text-amber-800/60 focus:border-amber-500"
                                                                        />
                                                                        <Input
                                                                            value={party.email || ""}
                                                                            onChange={(e) => updateParty(i, "email", e.target.value)}
                                                                            placeholder="Correo electrónico"
                                                                            className="h-8 text-xs rounded-md border-slate-200 bg-white"
                                                                        />
                                                                        <Input
                                                                            value={party.address || ""}
                                                                            onChange={(e) => updateParty(i, "address", e.target.value)}
                                                                            placeholder="Dirección de notificación"
                                                                            className="h-8 text-xs rounded-md border-slate-200 bg-white"
                                                                        />
                                                                    </div>
                                                                ) : (
                                                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                                                                        <Input
                                                                            value={party.identification}
                                                                            onChange={(e) => updateParty(i, "identification", e.target.value)}
                                                                            placeholder="Doc. (ej. CC 73.737.890 de Bogotá / NIT)"
                                                                            className="h-8 text-xs rounded-md border-slate-200 bg-white"
                                                                        />
                                                                        <Input
                                                                            value={party.email || ""}
                                                                            onChange={(e) => updateParty(i, "email", e.target.value)}
                                                                            placeholder="Correo electrónico"
                                                                            className="h-8 text-xs rounded-md border-slate-200 bg-white"
                                                                        />
                                                                        <Input
                                                                            value={party.address || ""}
                                                                            onChange={(e) => updateParty(i, "address", e.target.value)}
                                                                            placeholder="Dirección de notificación"
                                                                            className="h-8 text-xs rounded-md border-slate-200 bg-white"
                                                                        />
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>

                                            <div className="ddv2-field-group p-4 bg-white border border-slate-100 rounded-2xl shadow-sm">
                                                <div className="ddv2-field-label text-indigo-900 font-black uppercase tracking-wider mb-2 flex items-center gap-2">
                                                    <FileText className="h-4 w-4 text-indigo-500" /> Pretensiones & Puntos Clave del Pedimento
                                                </div>
                                                <p className="text-[11px] text-slate-500 mb-2 font-normal">
                                                    Detalle las pretensiones principales, subsidiarias, pedimentos económicos y normas o puntos clave.
                                                </p>
                                                <Textarea
                                                    value={form.keyPoints}
                                                    onChange={(e) => updateField("keyPoints", e.target.value)}
                                                    placeholder="Especifique de manera ordenada las pretensiones concretas y aspectos determinantes de la solicitud..."
                                                    className="min-h-[140px] text-sm leading-relaxed border-slate-200 focus:border-indigo-500 rounded-xl"
                                                />
                                            </div>



                                            <div className="flex justify-between mt-6">
                                                <Button variant="outline" onClick={() => setStep(1)}><ChevronLeft className="h-4 w-4" /> Anterior</Button>
                                                <Button onClick={() => setStep(3)} disabled={!isStepValid(2)} className="bg-indigo-600 text-white">Siguiente <ChevronRight className="h-4 w-4" /></Button>
                                            </div>
                                        </div>
                                    )}

                                    {/* Step 3 Content */}
                                    {step === 3 && (
                                        <div className="ddv2-animate-left space-y-6">
                                            {/* 1. Jurisdiction Selection */}
                                            <div className="ddv2-field-group p-4 bg-white border border-slate-100 rounded-2xl shadow-sm">
                                                <div className="ddv2-field-label text-indigo-900 font-black uppercase tracking-wider mb-4 flex items-center gap-2">
                                                    <Globe className="h-4 w-4 text-indigo-500" /> Jurisdicción
                                                </div>
                                                <Select value={form.jurisdiction} onValueChange={(v) => updateField("jurisdiction", v)}>
                                                    <SelectTrigger className="h-12 border-2 border-slate-100 hover:border-indigo-200 rounded-xl bg-white transition-all">
                                                        <SelectValue placeholder="Seleccione la jurisdicción..." />
                                                    </SelectTrigger>
                                                    <SelectContent className="rounded-xl border-slate-200 shadow-2xl bg-white z-[100]">
                                                        {Object.entries(JURISDICTIONS).map(([k, v]) => (
                                                            <SelectItem key={k} value={k} className="py-2.5 font-medium text-slate-700">
                                                                {v}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            </div>

                                            {/* 2. Complexity Selection (Grid of cards as requested) */}
                                            <div className="ddv2-field-group p-4 bg-white border border-slate-100 rounded-2xl shadow-sm">
                                                <div className="ddv2-field-label text-indigo-900 font-black uppercase tracking-wider mb-4 flex items-center gap-2">
                                                    <Gauge className="h-4 w-4 text-indigo-500" /> Complejidad
                                                </div>
                                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                                    {Object.entries(COMPLEXITY_LEVELS).map(([key, level]) => (
                                                        <button
                                                            key={key}
                                                            onClick={() => updateField("complexity", key as any)}
                                                            className={`flex flex-col items-center justify-center p-2.5 rounded-xl text-center border transition-all duration-300 group ${form.complexity === key ? 'bg-indigo-50/50 border-indigo-500 ring-2 ring-indigo-500/5' : 'bg-white border-slate-100 hover:border-slate-200 hover:bg-slate-50/50'}`}
                                                        >
                                                            <span className={`text-xs font-bold mb-0.5 ${form.complexity === key ? 'text-indigo-600' : 'text-slate-700'}`}>
                                                                {level.label}
                                                            </span>
                                                            <span className={`text-[9px] leading-tight font-medium ${form.complexity === key ? 'text-indigo-400' : 'text-slate-400'}`}>
                                                                {level.desc}
                                                            </span>
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>

                                            {/* 3.5. Seleccionar Hoja Membretada (Opcional) */}
                                            <div className="ddv2-field-group p-4 bg-white border border-slate-100 rounded-2xl shadow-sm">
                                                <div className="ddv2-field-label text-indigo-900 font-black uppercase tracking-wider mb-3 flex items-center gap-2">
                                                    <FileText className="h-4 w-4 text-indigo-500" /> Hoja Membretada (Opcional)
                                                </div>
                                                <Select value={form.template_path || "none"} onValueChange={(v) => updateField("template_path", v)}>
                                                    <SelectTrigger className="h-12 border-2 border-slate-100 hover:border-indigo-200 rounded-xl bg-white transition-all">
                                                        <SelectValue placeholder="-- Sin hoja membretada (Estándar) --" />
                                                    </SelectTrigger>
                                                    <SelectContent className="rounded-xl border-slate-200 shadow-2xl bg-white z-[100]">
                                                        <SelectItem value="none">-- Sin hoja membretada (Estándar) --</SelectItem>
                                                        {letterheadTemplates.map((tmpl: any) => {
                                                            const name = tmpl.name || (typeof tmpl === 'string' ? tmpl.split("/").pop() : "Plantilla");
                                                            const value = tmpl.path || tmpl;
                                                            return (
                                                                <SelectItem key={value} value={value}>
                                                                    {name}
                                                                </SelectItem>
                                                            );
                                                        })}
                                                    </SelectContent>
                                                </Select>
                                            </div>

                                            {/* 4. Resumen ULDE */}
                                            <div className="p-5 bg-indigo-50/30 border border-indigo-100 rounded-2xl ddv2-animate-up">
                                                <div className="text-[11px] font-black text-indigo-900 uppercase tracking-widest mb-3 flex items-center gap-2">
                                                    <CheckCircle2 className="h-4 w-4 text-indigo-500" /> Resumen ULDE
                                                </div>
                                                <div className="space-y-2">
                                                    <div className="flex justify-between text-xs">
                                                        <span className="text-slate-500">Categoría:</span>
                                                        <span className="font-bold text-slate-700">
                                                            {selectedCategory ? `${ULDE_CATEGORIES[selectedCategory as keyof typeof ULDE_CATEGORIES]?.emoji} ${ULDE_CATEGORIES[selectedCategory as keyof typeof ULDE_CATEGORIES]?.label}` : 'No seleccionada'}
                                                        </span>
                                                    </div>
                                                    <div className="flex justify-between text-xs">
                                                        <span className="text-slate-500">Documento:</span>
                                                        <span className="font-bold text-slate-700">{form.documentType || 'No especificado'}</span>
                                                    </div>
                                                    <div className="flex justify-between text-xs">
                                                        <span className="text-slate-500">Partes:</span>
                                                        <span className="font-bold text-slate-700">{form.parties.length}</span>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="flex justify-between items-center pt-4">
                                                <Button
                                                    variant="ghost"
                                                    onClick={() => setStep(2)}
                                                    className="text-slate-500 font-bold hover:text-indigo-600"
                                                >
                                                    <ChevronLeft className="h-4 w-4 mr-1" /> Anterior
                                                </Button>
                                                <Button
                                                    onClick={handleSubmit}
                                                    disabled={loading || !isStepValid(3)}
                                                    className="bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white font-bold px-10 h-12 rounded-xl shadow-xl shadow-indigo-100 transition-all active:scale-95 disabled:opacity-50 disabled:grayscale disabled:shadow-none"
                                                >
                                                    {loading ? (
                                                        <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Generando...</>
                                                    ) : (
                                                        <>Generar Documento <Sparkles className="h-4 w-4 ml-2" /></>
                                                    )}
                                                </Button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </ResizablePanel>
                            {!isMobile && <ResizableHandle withHandle className="bg-slate-200" />}
                        </>
                    )}

                    {(!isMobile || mobileTab === "preview") && (
                        <ResizablePanel defaultSize={!isMobile && showForm ? 67 : 100}>
                            <div className={`ddv2-preview-panel ${isMobile ? 'w-full pb-36 px-2' : 'h-full'} overflow-y-auto`} ref={previewRef}>
                                {isMobile && (
                                    <div className="pt-2 pb-2 flex items-center justify-between">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => setMobileTab("form")}
                                            className="h-8 text-xs font-bold gap-1 bg-white shadow-sm border-slate-200 text-slate-700"
                                        >
                                            <ChevronLeft className="w-3.5 h-3.5" /> Editar Parámetros
                                        </Button>
                                    </div>
                                )}
                                {loading ? (
                                <div className="ddv2-animate-up max-w-3xl mx-auto">
                                    <div className="ddv2-preview-doc">
                                        <div className="flex items-center gap-3 mb-6">
                                            <Loader2 className="h-6 w-6 text-indigo-500 animate-spin" />
                                            <div className="text-sm font-bold">Generando documento...</div>
                                        </div>
                                        <div className="space-y-4">
                                            <div className="ddv2-shimmer h-8 w-3/4" />
                                            <div className="ddv2-shimmer h-4 w-full" />
                                            <div className="ddv2-shimmer h-4 w-5/6" />
                                        </div>
                                    </div>
                                </div>
                            ) : result ? (
                                /* Document Preview */
                                <div className="ddv2-animate-right max-w-3xl mx-auto">
                                    {/* Status Bar */}
                                    <div className="ddv2-status-bar">
                                        <div className="flex items-center gap-4 flex-1">
                                            {/* Focus Toggle */}
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="text-white hover:bg-white/20 rounded-lg px-2"
                                                onClick={() => setShowForm(!showForm)}
                                            >
                                                {showForm ? <Maximize2 className="h-4 w-4" /> : <Minimize2 className="h-4 w-4" />}
                                            </Button>

                                            {/* Confidence Ring */}
                                            <div className="ddv2-confidence-ring">
                                                <svg width="60" height="60">
                                                    <circle cx="30" cy="30" r="24" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="4" />
                                                    <circle
                                                        cx="30"
                                                        cy="30"
                                                        r="24"
                                                        fill="none"
                                                        stroke={confidenceColor}
                                                        strokeWidth="4"
                                                        strokeLinecap="round"
                                                        strokeDasharray={circumference}
                                                        strokeDashoffset={circumference - strokeDash}
                                                    />
                                                </svg>
                                                <span className="text-white text-sm font-bold">{confidencePercent}%</span>
                                            </div>
                                            <div>
                                                <div className="text-white text-sm font-semibold">Confianza del Documento</div>
                                                <div className="text-indigo-200 text-xs">{result.document_type} · {JURISDICTIONS[result.jurisdiction || ""] || result.jurisdiction}</div>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2 flex-wrap">
                                            {result.colombian_compliance && (
                                                <span className="ddv2-compliance-badge bg-emerald-500/20 text-emerald-300">
                                                    <Shield className="h-3 w-3" /> Cumple normativa CO
                                                </span>
                                            )}
                                            <span className="ddv2-compliance-badge bg-indigo-500/20 text-indigo-300">
                                                <CheckCircle2 className="h-3 w-3" /> v1
                                            </span>
                                        </div>
                                    </div>

                                    {/* Document Content */}
                                    <div className="ddv2-preview-doc">
                                        <div className="legal-doc-content">
                                            <ReactMarkdown
                                                remarkPlugins={[remarkGfm]}
                                                components={{
                                                    p({ children }) {
                                                        const textContent = React.Children.toArray(children).join('').toLowerCase();
                                                        if (textContent.includes('notas adicionales') || textContent.includes('notas de validación')) {
                                                            return (
                                                                <div className="my-8 p-6 bg-blue-50/80 backdrop-blur-sm border-l-4 border-blue-600 rounded-r-xl ddv2-animate-up shadow-sm">
                                                                    <div className="text-blue-900 font-black uppercase tracking-[0.2em] text-[10px] mb-3 flex items-center gap-2">
                                                                        <Shield className="h-4 w-4 text-blue-600" /> NOTAS DE INTELIGENCIA LEGAL
                                                                    </div>
                                                                    <div className="text-blue-800 text-sm font-medium leading-relaxed italic">{renderNodeWithChips(children)}</div>
                                                                </div>
                                                            );
                                                        }
                                                        return <p className="mb-6 leading-relaxed text-slate-800">{renderNodeWithChips(children)}</p>;
                                                    },
                                                    li({ children }) { return <li className="mb-2 leading-relaxed text-slate-800">{renderNodeWithChips(children)}</li>; },
                                                    h1({ children }) { return <h1 className="text-3xl font-black text-center mb-10 border-b-2 border-slate-900 pb-4 uppercase tracking-wider">{renderNodeWithChips(children)}</h1>; },
                                                    h2({ children }) { return <h2 className="text-xl font-bold mb-6 border-l-4 border-indigo-900 pl-4 uppercase tracking-normal">{renderNodeWithChips(children)}</h2>; }
                                                }}
                                            >
                                                {displayContent}
                                            </ReactMarkdown>
                                        </div>
                                    </div>

                                    {/* Action bar */}
                                    <div className="ddv2-action-bar-sticky">
                                        <div className="flex gap-2">
                                            <Button variant="outline" onClick={handleReset} className="gap-2 text-xs font-bold border-slate-300">
                                                <RotateCcw className="h-3.5 w-3.5" /> RECOMENZAR
                                            </Button>
                                            <Button variant="outline" onClick={() => copyToClipboard(displayContent)} className="gap-2 text-xs font-bold border-slate-300">
                                                <Copy className="h-3.5 w-3.5" /> COPIAR
                                            </Button>

                                        </div>

                                        <div className="flex gap-2 flex-wrap">
                                            {isCompleted ? (
                                                <button
                                                    onClick={() => navigate(`/dashboard/collab/${finalDocId}`, {
                                                        state: {
                                                            content: displayContent,
                                                            documentType: form.documentType,
                                                            isNew: true,
                                                            templatePath: form.template_path
                                                        }
                                                    })}
                                                    className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-indigo-600 text-white font-bold text-xs hover:bg-indigo-700 transition-all shadow-md active:scale-95"
                                                >
                                                    <Users className="h-3.5 w-3.5" /> EDITAR COLABORATIVAMENTE
                                                </button>
                                            ) : (
                                                <button disabled className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-100 text-slate-400 font-bold text-xs cursor-not-allowed opacity-60 border border-slate-200">
                                                    <Loader2 className="h-3 w-3 animate-spin" /> EDITAR COLABORATIVAMENTE (Espere...)
                                                </button>
                                            )}

                                            <div className={`ddv2-dropdown ${showDownloadMenu ? "open" : ""}`} ref={downloadRef}>
                                                <button
                                                    onClick={() => setShowDownloadMenu(!showDownloadMenu)}
                                                    className="inline-flex items-center gap-2 px-5 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl font-bold text-xs shadow-lg hover:from-emerald-700 transition-all active:scale-95 whitespace-nowrap"
                                                >
                                                    <FileDown className="h-4 w-4" /> DESCARGAR <ChevronDown className={`h-3 w-3 transition-transform ${showDownloadMenu ? "ddv2-rotate-180" : ""}`} />
                                                </button>

                                                <div className="ddv2-dropdown-menu">
                                                    {downloadUrl && (
                                                        <a
                                                            href={downloadUrl}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="ddv2-dropdown-item"
                                                            onClick={() => setShowDownloadMenu(false)}
                                                        >
                                                            <FileDown className="h-4 w-4 text-emerald-600" />
                                                            <span>Word (.docx) - Alta Calidad</span>
                                                        </a>
                                                    )}
                                                    <button
                                                        onClick={() => { handleDownloadPDF(); setShowDownloadMenu(false); }}
                                                        className="ddv2-dropdown-item"
                                                    >
                                                        <FileText className="h-4 w-4 text-indigo-600" />
                                                        <span>PDF (.pdf) - Legal Pro</span>
                                                    </button>
                                                    {!downloadUrl && isCompleted && (
                                                        <button
                                                            onClick={() => { handleDownloadLocal(); setShowDownloadMenu(false); }}
                                                            className="ddv2-dropdown-item"
                                                        >
                                                            <FileDown className="h-4 w-4 text-slate-600" />
                                                            <span>Markdown (.md) - Local</span>
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="ddv2-welcome ddv2-animate-up">
                                    <div className="ddv2-welcome-icon">
                                        <FileText className="h-10 w-10 text-white" />
                                    </div>
                                    <h2 className="text-2xl font-bold text-slate-800 mb-2">Redactor Inteligente de Documentos</h2>
                                    <p className="text-slate-500 text-sm max-w-md mb-8 leading-relaxed">
                                        Complete el formulario en el panel izquierdo para generar documentos jurídicos personalizados con cumplimiento normativo colombiano.
                                    </p>
                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-lg">
                                        {[
                                            { icon: "📝", label: "Contratos", desc: "Laborales, comerciales, arrendamiento" },
                                            { icon: "⚖️", label: "Peticiones", desc: "Derechos de petición, tutelas" },
                                            { icon: "🏛️", label: "Societarios", desc: "Actas, estatutos, poderes" },
                                        ].map((c, i) => (
                                            <div key={i} className="p-4 rounded-xl border border-slate-200 bg-white text-center hover:border-indigo-300 hover:shadow-sm transition-all">
                                                <div className="text-2xl mb-1">{c.icon}</div>
                                                <div className="text-xs font-bold text-slate-700">{c.label}</div>
                                                <div className="text-[10px] text-slate-400 mt-0.5">{c.desc}</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </ResizablePanel>
                    )}
                </ResizablePanelGroup>
            </div>
            {/* Modal para completar placeholders */}
            <PlaceholderFillerModal
                placeholders={placeholders}
                isOpen={isFillerModalOpen}
                onClose={() => setIsFillerModalOpen(false)}
                onApply={handleReplaceMultiplePlaceholders}
            />
            <EditVariableModal
                isOpen={Boolean(editingVariable)}
                variableName={editingVariable || ""}
                initialValue=""
                onClose={() => setEditingVariable(null)}
                onSave={handleSaveSingleVariable}
            />
        </div>
    );
}
